import React, { useState, useEffect, useRef } from 'react';
import { Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { comprehensiveSearch, getSearchSuggestions } from '../../utils/search';
import '../../assets/scss/components/_search-bar.scss';

const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchStats, setSearchStats] = useState(null);
  const [searchSuggestionsAvailable, setSearchSuggestionsAvailable] = useState(false);
  const searchTimeoutRef = useRef(null);
  const suggestionsTimeoutRef = useRef(null);

  // Initialize search suggestions availability (disabled for now due to CORS issues)
  useEffect(() => {
    // Disable search suggestions to prevent CORS errors
    setSearchSuggestionsAvailable(false);
    setSearchStats({ data: [] });
  }, []);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeouts
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }

    // Get suggestions for queries with 2+ characters (only if available)
    if (query.length >= 2 && searchSuggestionsAvailable) {
      suggestionsTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await getSearchSuggestions(query, 5);
          if (response && response.data && response.data.data) {
            setSuggestions(response.data.data);
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        }
      }, 300);
    } else {
      setSuggestions([]);
    }

    // Perform search for queries with 3+ characters
    if (query.length >= 3) {
      setIsLoading(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await comprehensiveSearch(query, 5);
          if (response && response.data && response.data.data) {
            setSearchResults(response.data.data);
            setIsDropdownOpen(true);
          } else {
            setSearchResults(null);
          }
        } catch (error) {
          console.error('Error performing search:', error);
          setSearchResults(null);
        } finally {
          setIsLoading(false);
        }
      }, 500);
    } else {
      setSearchResults(null);
      setIsDropdownOpen(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setSuggestions([]);
    // Trigger search with the suggestion
    handleSearchChange({ target: { value: suggestion } });
  };

  // Build app route for a result
  const getResultUrl = (type, result) => {
    if (type === 'products') {
      return `/product-detail?productId=${result.id}`;
    }
    if (type === 'events') {
      return `/event-detail?event_id=${result.id}`;
    }
    if (type === 'oem_companies') {
      const q = result.oem_brandname || result.oem_companyname || result.id;
      return `/oem-list?search=${encodeURIComponent(q)}`;
    }
    return '#';
  };

  // Get icon for result type
  const getResultIcon = (type) => {
    switch (type) {
      case 'products':
        return 'package';
      case 'events':
        return 'calendar';
      case 'oem_companies':
        return 'building';
      default:
        return 'search';
    }
  };

  // Get display name for result
  const getResultDisplayName = (result, type) => {
    switch (type) {
      case 'products':
        return result.name || result.title || `Product ${result.id}`;
      case 'events':
        return result.title || result.event_name || `Event ${result.id}`;
      case 'oem_companies':
        return result.oem_brandname || result.oem_companyname;
      default:
        return 'Unknown';
    }
  };

  // Get subtitle for result
  const getResultSubtitle = (result, type) => {
    switch (type) {
      case 'products':
        return result.brand || result.category_name;
      case 'events':
        return result.event_type || result.oem_brandname || result.location;
      case 'oem_companies':
        return result.oem_companyname || result.city;
      default:
        return '';
    }
  };

  // Render search results
  const renderSearchResults = () => {
    if (!searchResults) return null;

    const resultTypes = ['products', 'events', 'oem_companies'];
    let hasResults = false;

    return (
      <div className="search-results">
        {resultTypes.map(type => {
          const results = searchResults[type] || [];
          if (results.length > 0) hasResults = true;

          return results.length > 0 ? (
            <div key={type} className="result-section">
              <div className="result-section-header">
                <FeatherIcon icon={getResultIcon(type)} className="me-2" size={16} />
                <span className="text-capitalize">{type.replace('_', ' ')}</span>
                <span className="result-count">({results.length})</span>
              </div>
              {results.map((result, index) => (
                <Link
                  key={`${type}-${result.id || index}`}
                  className="result-item"
                  to={getResultUrl(type, result)}
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults(null);
                    setSuggestions([]);
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="result-icon">
                    <FeatherIcon icon={getResultIcon(type)} size={16} />
                  </div>
                  <div className="result-content">
                    <div className="result-title">{getResultDisplayName(result, type)}</div>
                    <div className="result-subtitle">{getResultSubtitle(result, type)}</div>
                  </div>
                  <div className="result-score">
                    {result.search_score && (
                      <span className="badge bg-light text-dark">{result.search_score}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : null;
        })}
        {!hasResults && (
          <div className="no-results">
            <FeatherIcon icon="search" size={20} />
            <span>No results found for "{searchQuery}"</span>
          </div>
        )}
      </div>
    );
  };

  // Render suggestions
  const renderSuggestions = () => {
    if (suggestions.length === 0) return null;

    return (
      <div className="search-suggestions">
        <div className="suggestion-header">
          <FeatherIcon icon="lightbulb" size={16} className="me-2" />
          <span>Suggestions</span>
        </div>
        {suggestions.map((suggestion, index) => (
          <div
            key={index}
            className="suggestion-item"
            onClick={() => handleSuggestionClick(suggestion)}
          >
            <FeatherIcon icon="search" size={14} className="me-2" />
            <span>{suggestion}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="search-container">
      <Dropdown isOpen={isDropdownOpen} toggle={() => setIsDropdownOpen(!isDropdownOpen)}>
        <DropdownToggle tag="div" className="search-input-container">
          <div className="search-input-wrapper">
            <FeatherIcon icon="search" className="search-icon" size={18} />
            <Input
              type="text"
              placeholder="Search products, events, stores..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
              onFocus={() => {
                if (searchResults || suggestions.length > 0) {
                  setIsDropdownOpen(true);
                }
              }}
            />
            {isLoading && (
              <div className="search-loading">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults(null);
                  setSuggestions([]);
                  setIsDropdownOpen(false);
                }}
              >
                <FeatherIcon icon="x" size={16} />
              </button>
            )}
          </div>
        </DropdownToggle>
        
        <DropdownMenu className="search-dropdown">
          {renderSuggestions()}
          {renderSearchResults()}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default SearchBar; 